<template>
  <div :style="`width:${cardWidth}px;height:${cardHeight}px;margin-left:${padding}px`"
       class="scratchcard"
  >
    <canvas class="scratchcard-overlay"
            ref="backgroundCanvas"
            v-show="overlayLoaded"></canvas>
    <canvas @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @touchend="handleMouseUp"
            @touchmove="handleMouseMove"
            @touchstart="handleMouseDown"
            class="scratchcard-overlay"
            ref="canvas"></canvas>
    <div class="scratchcard-content"
         v-if="overlayLoaded">
      <slot></slot>
    </div>
  </div>
</template>

<script>

import BRUSH from '../../assets/brush.png';


const stride = 32;

function calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
  let ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return {width: srcWidth * ratio, height: srcHeight * ratio};
}

function getTotalPixels(ctx, width, height, stride) {
  if (!stride || stride < 1) stride = 1;
  const pixels = ctx.getImageData(0, 0, width, height);
  let total = 0;
  for (let i = 0; i < pixels.data.length; i += stride) {
    if (parseInt(pixels.data[i], 10) !== 0) total++;
  }
  return total;
}

function getFilledPercent(ctx, width, height, total, stride) {
  if (!stride || stride < 1) stride = 1;
  const pixels = ctx.getImageData(0, 0, width, height);

  let count = 0;
  for (let i = 0; i < pixels.data.length; i += stride) {
    if (parseInt(pixels.data[i], 10) !== 0) count++;
  }
  return 100 - Math.round(count / total * 100);
}

function getMouse(e, canvas) {
  const {left, top} = canvas.getBoundingClientRect();
  const touch = e.touches && e.touches[0];
  if (touch) {
    return {x: touch.clientX - left, y: touch.clientY - top};
  } else {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    return {x: e.pageX - left - scrollLeft, y: e.pageY - top - scrollTop};
  }
}

function distanceBetween(point1, point2) {
  return Math.sqrt(
      Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2),
  );
}

function angleBetween(point1, point2) {
  return Math.atan2(point2.x - point1.x, point2.y - point1.y);
}

export default {
  name: 'ScratchCard',

  props: {
    imageUrl: String,
    backUrl: String,
    finishPercent: Number,
    forceReveal: Boolean,
    isDemo: Boolean
  },

  data() {
    return {
      overlayLoaded: false,
      isDrawing: false,
      isFinished: false,
      canvas: undefined,
      backgroundCanvas: undefined,
      ctx: undefined,
      ctxBackground: undefined,
      lastPoint: undefined,
      brush: new Image(),
      totalPixels: 0,
      cardWidth: 0,
      cardHeight: 0,
      brushUrl: undefined, // BRUSH, -- fix for new chrome versions
      padding: 15,
      started: false
    };
  },

  methods: {
    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.backgroundCanvas = this.$refs.backgroundCanvas;
      this.ctxBackground = this.backgroundCanvas.getContext('2d');
    },

    drawImage() {
      if (this.backUrl) {
        const backImage = new Image();
        backImage.crossOrigin = 'Anonymous';
        backImage.src = this.backUrl;
        backImage.onload = () => {

          let imgSizeWidth = backImage.width;
          let imgSizeHeight = backImage.height;

          // Use container width if available (for desktop constraints)
          let containerEl = this.$el.closest('.bg') || this.$el.closest('[role="region"]');
          let screenWidth = containerEl ? containerEl.offsetWidth : window.innerWidth;
          let screenHeight = window.innerHeight;

          let padding = this.padding * 2;

          let aspect = calculateAspectRatioFit(imgSizeWidth, imgSizeHeight, screenWidth - padding, screenHeight);

          this.canvas.width = this.cardWidth = aspect.width;
          this.canvas.height = this.cardHeight = aspect.height;

          this.backgroundCanvas.width = this.cardWidth = aspect.width;
          this.backgroundCanvas.height = this.cardHeight = aspect.height;

          this.ctxBackground.drawImage(backImage, 0, 0, this.cardWidth, this.cardHeight);
          this.drawOverlayImage();
        };
      } else {
        this.drawOverlayImage();
      }

    },

    drawOverlayImage() {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.src = this.imageUrl;
      image.onload = () => {
        this.ctx.drawImage(image, 0, 0, this.cardWidth, this.cardHeight);
        this.overlayLoaded = true;
        this.totalPixels = getTotalPixels(this.ctx, this.cardWidth, this.cardHeight, stride);
      };
    },

    prepBrush() {
      if (this.brushUrl) {
        this.brush.crossOrigin = 'Anonymous';
        this.brush.src = this.brushUrl;
      }
    },

    scratchAt(x, y) {
      if (this.brushUrl) {
        this.ctx.drawImage(
            this.brush,
            x - this.brush.width / 2,
            y - this.brush.height / 2,
        );
      } else {
        this.ctx.beginPath();
        this.ctx.arc(x, y, 25, 0, 2 * Math.PI, false);
        this.ctx.fill();
      }
    },

    handleMouseDown(e) {
      this.isDrawing = true;
      this.lastPoint = getMouse(e, this.canvas);
    },

    handleMouseUp() {
      this.isDrawing = false;
    },

    handleMouseMove(e) {
      if (!this.isDrawing) return;

      e.preventDefault();
      const currentPoint = getMouse(e, this.canvas);
      const distance = distanceBetween(this.lastPoint, currentPoint);
      const angle = angleBetween(this.lastPoint, currentPoint);

      let x, y;
      for (let i = 0; i < distance; i++) {
        x = this.lastPoint.x + Math.sin(angle) * i;
        y = this.lastPoint.y + Math.cos(angle) * i;
        this.ctx.globalCompositeOperation = 'destination-out';
        this.scratchAt(x, y);
      }
      this.lastPoint = currentPoint;
      this.handlePercentage(
          getFilledPercent(this.ctx, this.cardWidth, this.cardHeight, this.totalPixels, stride),
      );
    },

    handlePercentage(filledInPixels = 0) {
      if (this.isDemo) {
        if (filledInPixels >= this.finishPercent)
          this.$emit('onPercentageComplete', 100);
        else
          this.$emit('onPercentageComplete', filledInPixels);
      }
      if (filledInPixels >= this.finishPercent) {

        this.reveal();
      }
      if (!this.started) {
        this.started = true;
        this.$emit('onPercentageComplete', filledInPixels);
      }
    },

    reveal() {
      if (!this.isFinished) {
        this.canvas.parentNode.removeChild(this.canvas);
        this.$emit('onComplete');
      }
      this.isFinished = true;
    },
  },

  watch: {
    forceReveal(val) {
      if (val) this.reveal();
    },
  },

  mounted() {
    this.initCanvas();
    this.drawImage();
    this.prepBrush();
  },
};
</script>

<style scoped>
.scratchcard {
  position: relative;
  display: block;
}

.scratchcard > * {
  position: absolute;
  width: 100%;
  height: 100%;
  display: block;
}

.scratchcard-overlay {
  z-index: 1;
}
</style>